import { useQuery } from '@tanstack/react-query';
import { getContent } from './endpoints';
import { useCacheManager } from '../../hooks/useCacheManager';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { checkPageUpdates, MAIN_PAGES } from '../cacheService';
import { queryClient } from '../queryClientService';

export const getComponentsByKey = (key) => {
    const pageData = MAIN_PAGES.find(page => page.key === key);
    return pageData ? pageData.components : [];
};

// Fonction commune pour gérer les vérifications fraîcheur et les mises à jour
const useCommonCacheLogic = (queryKey, fetchFn, options = {}) => {
    const [isFreshnessFetching, setIsFreshnessFetching] = useState(false);
    const location = useLocation();
    const previousPathRef = useRef(location.pathname);
    const isBackgroundCheckRef = useRef(false); // Pour les vérifications silencieuses

    // Vérifier si les mises à jour sont nécessaires lors des changements de route
    useEffect(() => {
        if (previousPathRef.current !== location.pathname) {
            previousPathRef.current = location.pathname;
            isBackgroundCheckRef.current = false; // Réinitialiser lors d'un vrai changement de page

            const checkUpdates = async () => {
                try {
                    setIsFreshnessFetching(true);
                    
                    // Fonction spécifique de vérification (fournie via options)
                    const needsUpdate = options.checkUpdatesFn ? 
                        await options.checkUpdatesFn() : false;

                    if (needsUpdate) {
                        console.debug(`Mise à jour détectée pour ${JSON.stringify(queryKey)}`);
                        await queryClient.invalidateQueries({
                            queryKey: queryKey,
                            exact: true
                        });
                        await queryClient.refetchQueries({
                            queryKey: queryKey,
                            exact: true
                        });
                    } else {
                        console.debug(`Aucune mise à jour pour ${JSON.stringify(queryKey)}`);
                    }
                } catch (error) {
                    console.error(`Erreur lors de la vérification pour ${JSON.stringify(queryKey)}:`, error);
                } finally {
                    // Délai court pour éviter les clignotements
                    setTimeout(() => setIsFreshnessFetching(false), 300);
                }
            };

            if (options.checkUpdatesFn) {
                checkUpdates();
            }
        }
    }, [location.pathname, queryKey, options.checkUpdatesFn]);

    // Configuration du gestionnaire de cache avec gestion silencieuse
    useCacheManager(
        queryKey,
        fetchFn,
        {
            onCacheHit: () => {
                console.debug(`Cache hit pour ${JSON.stringify(queryKey)}`);
            },
            onCacheMiss: () => {
                console.debug(`Cache miss pour ${JSON.stringify(queryKey)}`);
            },
            onCacheUpdate: () => {
                // Uniquement si ce n'est pas une vérification en arrière-plan
                if (!isBackgroundCheckRef.current) {
                    setIsFreshnessFetching(true);
                    setTimeout(() => setIsFreshnessFetching(false), 500);
                } else {
                    console.debug(`Mise à jour silencieuse du cache pour ${JSON.stringify(queryKey)}`);
                    // Simplement invalider et recharger sans changer l'état de chargement
                    queryClient.invalidateQueries({
                        queryKey: queryKey,
                        exact: true
                    }).then(() => {
                        return queryClient.refetchQueries({
                            queryKey: queryKey,
                            exact: true
                        });
                    }).catch(error => {
                        console.error(`Erreur lors de la mise à jour silencieuse pour ${JSON.stringify(queryKey)}:`, error);
                    });
                }
            },
            // Support pour les vérifications en arrière-plan
            backgroundCheck: true,
            onBackgroundCheckStart: () => {
                isBackgroundCheckRef.current = true;
            },
            onBackgroundCheckEnd: () => {
                isBackgroundCheckRef.current = false;
            },
            ...options.cacheManagerOptions
        }
    );

    return { isFreshnessFetching };
};

// Hook pour les requêtes de contenu de page statiques
export function useContentQuery(page, options = {}) {
    const components = getComponentsByKey(page);
    
    // Fonction spécifique pour vérifier les mises à jour de pages
    const checkPageContentUpdates = async () => {
        return await checkPageUpdates(page, components);
    };
    
    // Utiliser la logique commune avec vérification de mises à jour spécifique
    const { isFreshnessFetching } = useCommonCacheLogic(
        ['strapi-content', page],
        () => getContent({ page, components }),
        {
            checkUpdatesFn: checkPageContentUpdates,
            cacheManagerOptions: options.cacheManagerOptions || {}
        }
    );

    // Effectuer la requête principale
    const query = useQuery({
        queryKey: ['strapi-content', page],
        queryFn: async () => {
            const response = await getContent({ page, components });
            return response.data.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 60 * 60 * 1000, // 60 minutes
        ...options
    });

    // Renvoyer les données avec l'état de fraîcheur
    return {
        ...query,
        previousData: query.data,
        isFreshnessFetching
    };
}

// Hook pour les requêtes de collections (pages dynamiques)
export function useCollectionQuery(collections, slug, isWorkshop, isFreebie, options = {}) {
    const components = getComponentsByKey(collections);
    
    // Fonction spécifique pour vérifier les mises à jour de collections
    // Nous pourrions créer une fonction équivalente à checkPageUpdates pour les collections
    const checkCollectionUpdates = async () => {
        try {
            // Récupérer la version actuelle depuis Strapi
            const response = await getContent({ collections, slug, components }, true, isWorkshop, isFreebie);
            const latestData = response.data.data;
    
            // Récupérer la version en cache
            const cachedData = queryClient.getQueryData(['strapi-collection', collections, slug]);
    
            // Si pas de cache, considérer comme mise à jour nécessaire
            if (!cachedData) return true;
    
            // Comparer uniquement les timestamps updatedAt (pour un élément ou un tableau)
            if (Array.isArray(latestData)) {
                // Pour les collections multiples, vérifier si un des éléments a été mis à jour
                for (const item of latestData) {
                    const cachedItem = cachedData.find(c => c.id === item.id);
                    if (cachedItem && item.updatedAt && cachedItem.updatedAt) {
                        if (new Date(item.updatedAt) > new Date(cachedItem.updatedAt)) {
                            return true;
                        }
                    }
                }
                return false;
            } else {
                // Pour un élément unique
                if (latestData.updatedAt && cachedData.updatedAt) {
                    return new Date(latestData.updatedAt) > new Date(cachedData.updatedAt);
                }
            }
    
            // Si updatedAt n'est pas disponible, retourner false pour éviter des mises à jour inutiles
            return false;
        } catch (error) {
            console.error(`Erreur lors de la vérification des mises à jour pour collection ${collections}/${slug}:`, error);
            return false;
        }
    };
    
    // Utiliser la logique commune avec vérification de mises à jour spécifique
    const { isFreshnessFetching } = useCommonCacheLogic(
        ['strapi-collection', collections, slug],
        () => getContent({ collections, slug, components }, true, isWorkshop, isFreebie),
        {
            checkUpdatesFn: checkCollectionUpdates,
            cacheManagerOptions: options.cacheManagerOptions || {}
        }
    );
    // Effectuer la requête principale
    const query = useQuery({
        queryKey: ['strapi-collection', collections, slug],
        queryFn: async () => {
            const response = await getContent({ collections, slug, components }, true, isWorkshop, isFreebie);
            return response.data.data;
        },
        staleTime: 5 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
        ...options
    });

    // Renvoyer les données avec l'état de fraîcheur
    return {
        ...query,
        previousData: query.data,
        isFreshnessFetching
    };
}